import { icons } from '@/images';
import { useEffect, useCallback } from 'react';

interface SnackbarProps {
  readonly message: string;
  readonly isVisible: boolean;
  readonly onClose: () => void;
  /** Auto-hide duration in milliseconds. Default: 3000ms */
  readonly duration?: number;
  /** Snackbar type for styling */
  readonly type?: 'success' | 'error' | 'info';
  /** Position variant for different page layouts */
  readonly position?: 'default' | 'center' | 'top-center';
}

export const Snackbar = ({
  message,
  isVisible,
  onClose,
  duration = 3000,
  type = 'success',
  position = 'default',
}: SnackbarProps) => {
  // Auto-dismiss after duration
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isVisible, duration, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isVisible) return null;

  // Style based on type
  const typeStyles = {
    success: 'bg-[#F3FFF2]',
    error: 'bg-[#FFEBEE]',
    info: 'bg-[#E3F2FD]',
  };

  const textStyles = {
    success: 'text-[#00820B]',
    error: 'text-[#D32F2F]',
    info: 'text-[#1976D2]',
  };

  const positionStyles = {
    default:
      'fixed right-0 bottom-[70px] z-50 flex w-[calc(100%-var(--left-panel-width))] items-center justify-center p-5',
    center: 'fixed inset-x-0 bottom-[70px] z-50 flex items-center justify-center p-5',
    'top-center': 'fixed inset-x-0 top-[100px] z-50 flex items-center justify-center p-5',
  };

  return (
    <div className={positionStyles[position]}>
      <div
        className={`slide-up animate-slide-up flex items-center gap-2 rounded-full px-4 py-3 shadow-lg ${typeStyles[type]}`}
        role="alert"
      >
        {type === 'success' && (
          <img src={icons.CheckIcon} alt="check" className="h-[14px] w-5 flex-shrink-0" />
        )}
        {type === 'error' && (
          <svg
            className="h-5 w-5 flex-shrink-0 text-[#D32F2F]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )}
        {type === 'info' && (
          <svg
            className="h-5 w-5 flex-shrink-0 text-[#1976D2]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )}
        <span className={`label-md-w1 ${textStyles[type]}`}>{message}</span>
        <button
          type="button"
          onClick={handleClose}
          className="ml-2 flex-shrink-0 cursor-pointer opacity-70 transition-opacity hover:opacity-100"
          aria-label="Close"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};
